import { Message } from "../../domain/entities/message";
import { Equipment } from "../../domain/entities/equipment";
import { EquipmentAttributeDefinition } from "../../domain/entities/equipment-attribute-definition";
import { EquipmentInputFilter } from "../../domain/models/equipment-input-filter";
import { httpClient } from "../axios/axios-api";

class EquipmentRepository {
  async getAll(
    filter: EquipmentInputFilter,
  ): Promise<
    Message<{ data: Equipment[]; page: number; size: number; count: number }>
  > {
    const response = await httpClient.get("/equipment", { params: filter });
    return response.data;
  }

  async create(input: Equipment): Promise<Message<Equipment>> {
    const response = await httpClient.post("/equipment", { data: input });
    return response.data;
  }

  async update(input: Equipment): Promise<Message<boolean>> {
    const response = await httpClient.put(`/equipment/${input.id}`, {
      data: input,
    });
    return response.data;
  }

  async delete(id: string): Promise<Message<boolean>> {
    const response = await httpClient.delete(`/equipment/${id}`);
    return response.data;
  }

  async updateMyAttributes(
    id: string,
    attributes: string,
  ): Promise<Message<boolean>> {
    const response = await httpClient.put(`/equipment/my/${id}/attributes`, {
      data: { attributes },
    });
    return response.data;
  }

  async logMileage(
    id: string,
    month: number,
    year: number,
    kms: number,
  ): Promise<Message<boolean>> {
    const response = await httpClient.post(`/equipment/my/${id}/mileage`, {
      data: { month, year, kms },
    });
    return response.data;
  }

  async getAttributeDefinitions(filter: {
    equipmentTypeId?: string;
  }): Promise<
    Message<{
      data: EquipmentAttributeDefinition[];
      page: number;
      size: number;
      count: number;
    }>
  > {
    const response = await httpClient.get("/equipment/attributes/definition", {
      params: filter,
    });
    return response.data;
  }

  async createAttributeDefinition(
    input: EquipmentAttributeDefinition,
  ): Promise<Message<EquipmentAttributeDefinition>> {
    const response = await httpClient.post("/equipment/attributes/definition", {
      data: input,
    });
    return response.data;
  }

  async updateAttributeDefinition(
    input: EquipmentAttributeDefinition,
  ): Promise<Message<boolean>> {
    const response = await httpClient.put(
      `/equipment/attributes/definition/${input.id}`,
      { data: input },
    );
    return response.data;
  }

  async getMileageLogs(id: string): Promise<Message<any[]>> {
    const response = await httpClient.get(`/equipment/my/${id}/mileage`);
    return response.data;
  }

  async getAllMileageLogs(filter?: {
    month?: number;
    year?: number;
  }): Promise<Message<any[]>> {
    const response = await httpClient.get("/equipment/mileage", {
      params: filter,
    });
    return response.data;
  }

  async getMovements(id: string): Promise<Message<any[]>> {
    const response = await httpClient.get(`/equipment/${id}/movements`);
    return response.data;
  }

  async createMovement(id: string, data: any): Promise<Message<any>> {
    const response = await httpClient.post(`/equipment/${id}/movements`, {
      data,
    });
    return response.data;
  }

  async updateMovement(
    id: string,
    movementId: string,
    data: any,
  ): Promise<Message<any>> {
    const response = await httpClient.put(
      `/equipment/${id}/movements-update/${movementId}`,
      { data },
    );
    return response.data;
  }

  async getDocuments(id: string): Promise<Message<any[]>> {
    const response = await httpClient.get(`/equipment/${id}/documents`);
    return response.data;
  }

  async createDocument(id: string, data: any): Promise<Message<any>> {
    const response = await httpClient.post(`/equipment/${id}/documents`, {
      data,
    });
    return response.data;
  }

  async deleteDocument(docId: string): Promise<Message<boolean>> {
    const response = await httpClient.delete(`/equipment/documents/${docId}`);
    return response.data;
  }
}

export default new EquipmentRepository();
